export interface LocalScore {
  initials: string;
  score: number;
  tableId: string;
  createdAt: number;
}

const STORAGE_KEY = 'bop-local-scores-v1';
const MAX_SCORES_PER_TABLE = 10;

export function normalizeInitials(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 3);
}

function validScore(score: LocalScore): boolean {
  return (
    score.initials.length === 3 &&
    typeof score.tableId === 'string' &&
    score.tableId.length > 0 &&
    Number.isFinite(score.score) &&
    score.score >= 0 &&
    Number.isFinite(score.createdAt)
  );
}

export function rankLocalScores(scores: LocalScore[]): LocalScore[] {
  const byTable = new Map<string, LocalScore[]>();
  for (const score of scores) {
    if (!validScore(score)) continue;
    const tableScores = byTable.get(score.tableId) ?? [];
    tableScores.push(score);
    byTable.set(score.tableId, tableScores);
  }

  return [...byTable.entries()]
    .sort(([tableA], [tableB]) => tableA.localeCompare(tableB))
    .flatMap(([, tableScores]) =>
      tableScores
        .sort((a, b) => b.score - a.score || a.createdAt - b.createdAt)
        .slice(0, MAX_SCORES_PER_TABLE),
    );
}

export function loadLocalScores(): LocalScore[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]') as LocalScore[];
    return Array.isArray(stored) ? rankLocalScores(stored) : [];
  } catch {
    return [];
  }
}

export function saveLocalScore(score: Omit<LocalScore, 'createdAt'>): LocalScore[] {
  const scores = rankLocalScores([...loadLocalScores(), { ...score, createdAt: Date.now() }]);
  if (typeof localStorage === 'undefined') return scores;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scores));
  } catch {
    // Local score persistence is optional. Browsers can block storage entirely
    // or reject writes when the origin is out of quota; keep the just-computed
    // leaderboard usable for the current UI instead of turning a game-over save
    // into an application error.
  }
  return scores;
}
